import { Link, useNavigate } from '@tanstack/react-router';
import { type ReactNode, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { ApiError } from '@/lib/api/client';
import { useEvalRuns, useTriggerEvalRun } from '@/lib/api/eval-runs';

import { EMPTY_RUN_OPTIONS, RunOptions, type RunOptionsState, toCandidate } from './run-options';

export function RunBox({
  suiteId,
  rerunOf,
  title = 'Run',
  description,
  submitLabel = 'Run eval',
  initialLabel = '',
  initialOptions = EMPTY_RUN_OPTIONS,
  onClose,
}: {
  suiteId: string;
  rerunOf?: string;
  title?: string;
  description?: ReactNode;
  submitLabel?: string;
  initialLabel?: string;
  initialOptions?: RunOptionsState;
  onClose?: () => void;
}) {
  const navigate = useNavigate();
  const triggerRun = useTriggerEvalRun();
  const runsQuery = useEvalRuns({ page: 1, pageSize: 5 });
  const runningRun = runsQuery.data?.items.find((run) => run.status === 'running');
  const [label, setLabel] = useState(initialLabel);
  const [runOptions, setRunOptions] = useState<RunOptionsState>(initialOptions);
  const [error, setError] = useState<string | null>(null);

  const runEval = () => {
    setError(null);
    const candidate = toCandidate(runOptions);
    triggerRun.mutate(
      {
        suiteId,
        ...(label.trim() ? { label } : {}),
        ...(candidate ? { candidate } : {}),
        ...(rerunOf ? { rerunOf } : {}),
      },
      {
        onSuccess: (run) => navigate({ to: '/evals/runs/$runId', params: { runId: run.id } }),
        onError: (caught) =>
          setError(caught instanceof ApiError ? caught.message : 'Could not start the run.'),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {description && (
          <p className="text-sm text-[var(--color-muted-foreground)]">{description}</p>
        )}
        <Input
          placeholder="Optional label"
          maxLength={200}
          value={label}
          onChange={(event) => setLabel(event.target.value)}
        />
        <RunOptions value={runOptions} onChange={setRunOptions} />
        {error && <p className="text-sm text-[var(--color-destructive)]">{error}</p>}
        <div className="flex items-center gap-2">
          <Button onClick={runEval} disabled={Boolean(runningRun) || triggerRun.isPending}>
            {triggerRun.isPending ? 'Starting…' : submitLabel}
          </Button>
          {onClose && (
            <Button variant="outline" onClick={onClose} disabled={triggerRun.isPending}>
              Close
            </Button>
          )}
          {runningRun && (
            <Link
              to="/evals/runs/$runId"
              params={{ runId: runningRun.id }}
              className="text-sm hover:underline"
            >
              A run is in progress →
            </Link>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
